import React, { useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useParams, useSearchParams } from 'react-router-dom';
import { Modal } from 'react-bootstrap';
import { getCabPackage, listCabPackages } from '../../api/cabsPublic';
import CabsBreadcrumbs from './CabsBreadcrumbs';

const ensureArray = (value) => (Array.isArray(value) ? value : []);

const parseContentList = (value) => {
  if (!value) return [];
  if (Array.isArray(value)) return value.filter(Boolean);
  return String(value)
    .split('\n')
    .map((item) => item.replace(/^[-*]\s*/, '').trim())
    .filter(Boolean);
};

const parseItinerary = (value) => {
  if (!value) return [];
  if (Array.isArray(value)) return value;
  if (typeof value === 'string') {
    try {
      const parsed = JSON.parse(value);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }
  return [];
};

const CabsPackageDetail = () => {
  const { slug } = useParams();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const [pkg, setPkg] = useState(null);
  const [loading, setLoading] = useState(true);
  const [relatedPackages, setRelatedPackages] = useState([]);
  const [activeVariantId, setActiveVariantId] = useState('');
  const [activeImage, setActiveImage] = useState('');
  const [filters, setFilters] = useState({
    passengers: searchParams.get('passengers') || '',
    budgetMin: searchParams.get('budgetMin') || '',
    budgetMax: searchParams.get('budgetMax') || '',
    vehicleCategoryId: searchParams.get('vehicleCategoryId') || '',
    seatConfigId: searchParams.get('seatConfigId') || '',
  });

  useEffect(() => {
    let active = true;
    const load = async () => {
      setLoading(true);
      try {
        const res = await getCabPackage(slug);
        const next = res.data || res || null;
        if (!active) return;
        setPkg(next);
        setActiveImage(next?.coverImageUrl || next?.media?.find((item) => item.isCover)?.url || next?.media?.[0]?.url || '');
      } catch {
        if (!active) return;
        setPkg(null);
      } finally {
        if (active) setLoading(false);
      }
    };
    load();
    return () => {
      active = false;
    };
  }, [slug]);

  useEffect(() => {
    if (!pkg?.locationId) return;
    listCabPackages({ locationId: pkg.locationId, limit: 4 })
      .then((res) => {
        const items = res.data || [];
        setRelatedPackages(items.filter((item) => item.id !== pkg.id).slice(0, 3));
      })
      .catch(() => setRelatedPackages([]));
  }, [pkg?.id, pkg?.locationId]);

  const variants = ensureArray(pkg?.variants);
  const filteredVariants = useMemo(() => {
    const pax = filters.passengers ? Number(filters.passengers) : null;
    const minBudget = filters.budgetMin ? Number(filters.budgetMin) : null;
    const maxBudget = filters.budgetMax ? Number(filters.budgetMax) : null;
    return variants.filter((variant) => {
      if (filters.vehicleCategoryId && variant.vehicleCategoryId !== filters.vehicleCategoryId) return false;
      if (filters.seatConfigId && variant.seatConfigId !== filters.seatConfigId) return false;
      if (pax && variant.maxPax && pax > variant.maxPax) return false;
      const price = Number(variant.priceTotal || 0);
      if (minBudget !== null && price < minBudget) return false;
      if (maxBudget !== null && price > maxBudget) return false;
      return true;
    });
  }, [variants, filters]);

  useEffect(() => {
    const pool = filteredVariants.length ? filteredVariants : variants;
    const sorted = [...pool].sort((a, b) => Number(a.priceTotal || 0) - Number(b.priceTotal || 0));
    setActiveVariantId(sorted[0]?.id || '');
  }, [filteredVariants, variants]);

  const activeVariant = variants.find((variant) => variant.id === activeVariantId) || filteredVariants[0] || null;
  const gallery = useMemo(() => {
    const media = ensureArray(pkg?.media);
    if (pkg?.coverImageUrl && !media.some((item) => item.url === pkg.coverImageUrl)) {
      return [{ id: 'cover', url: pkg.coverImageUrl, altText: pkg.title, isCover: true }, ...media];
    }
    return media;
  }, [pkg]);
  const highlightItems = parseContentList(pkg?.highlights);
  const inclusionItems = parseContentList(pkg?.inclusions);
  const exclusionItems = parseContentList(pkg?.exclusions);
  const itineraryItems = parseItinerary(pkg?.itineraryJson);
  const enquiryQuery = new URLSearchParams(location.search || '');
  if (activeVariantId) enquiryQuery.set('packageVariantId', activeVariantId);
  if (filters.passengers) enquiryQuery.set('passengers', filters.passengers);
  const enquiryLink = `/cabs/packages/${slug}/enquiry${enquiryQuery.toString() ? `?${enquiryQuery.toString()}` : ''}`;

  if (loading) {
    return <div className="rounded-[24px] border border-slate-200 bg-white p-8 text-sm text-slate-500">Loading package...</div>;
  }

  if (!pkg) {
    return <div className="rounded-[24px] border border-slate-200 bg-white p-8 text-sm text-slate-500">Package not found.</div>;
  }

  return (
    <section className="space-y-6 pb-10">
      <CabsBreadcrumbs
        items={[
          { label: 'Home', to: '/' },
          { label: 'Cabs', to: '/cabs' },
          { label: 'Packages', to: '/cabs/packages' },
          { label: pkg.title },
        ]}
      />

      <div className="rounded-[32px] border border-slate-200 bg-white p-5 shadow-sm sm:p-6">
        <div className="grid gap-6 lg:grid-cols-[1.2fr_0.8fr]">
          <div>
            <div className="overflow-hidden rounded-[28px] bg-slate-100">
              {activeImage ? (
                <img src={activeImage} alt={pkg.title} className="h-[420px] w-full object-cover" />
              ) : (
                <div className="flex h-[420px] items-center justify-center text-xs text-slate-400">No image</div>
              )}
            </div>
            {gallery.length > 1 && (
              <div className="mt-3 grid grid-cols-4 gap-3">
                {gallery.slice(0, 8).map((image) => (
                  <button
                    key={image.id}
                    type="button"
                    onClick={() => setActiveImage(image.url)}
                    className={`overflow-hidden rounded-[18px] border ${activeImage === image.url ? 'border-[#0d6b66]' : 'border-slate-200'}`}
                  >
                    <img src={image.url} alt={image.altText || pkg.title} className="h-20 w-full object-cover" />
                  </button>
                ))}
              </div>
            )}
          </div>

          <div className="space-y-4">
            <div className="rounded-[28px] bg-[linear-gradient(135deg,#081a35_0%,#0d6b66_100%)] p-5 text-white shadow-sm">
              <p className="text-[11px] uppercase tracking-[0.22em] text-[#d7c08b]">{pkg.location?.name || 'Transport package'}</p>
              <h1 className="mt-3 text-3xl font-semibold">{pkg.title}</h1>
              <p className="mt-3 text-sm text-slate-100">{pkg.description || 'Curated intercity transport itinerary with dynamic variants and route support.'}</p>
              <div className="mt-4 flex flex-wrap gap-2">
                <span className="rounded-full bg-white/10 px-3 py-1 text-xs">{pkg.days} days</span>
                <span className="rounded-full bg-white/10 px-3 py-1 text-xs">{pkg.nights} nights</span>
                {activeVariant ? (
                  <span className="rounded-full bg-[#d2a54b] px-3 py-1 text-xs text-slate-950">
                    From INR {Number(activeVariant.priceTotal || 0).toLocaleString()}
                  </span>
                ) : null}
              </div>
            </div>

            <div className="sticky top-24 rounded-[28px] border border-slate-200 bg-[#fbfcfb] p-5 shadow-sm">
              <h2 className="text-lg font-semibold text-slate-900">Choose a vehicle setup</h2>
              <div className="mt-4 grid gap-3 sm:grid-cols-2">
                <div>
                  <label className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-400">Passengers</label>
                  <input
                    type="number"
                    min="1"
                    className="mt-2 w-full rounded-2xl border border-slate-200 px-3 py-2 text-sm"
                    value={filters.passengers}
                    onChange={(e) => setFilters((current) => ({ ...current, passengers: e.target.value }))}
                  />
                </div>
                <div>
                  <label className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-400">Vehicle type</label>
                  <select
                    className="mt-2 w-full rounded-2xl border border-slate-200 px-3 py-2 text-sm"
                    value={filters.vehicleCategoryId}
                    onChange={(e) => setFilters((current) => ({ ...current, vehicleCategoryId: e.target.value }))}
                  >
                    <option value="">All</option>
                    {Array.from(new Map(variants.map((variant) => [variant.vehicleCategory?.id, variant.vehicleCategory])).values())
                      .filter(Boolean)
                      .map((category) => (
                        <option key={category.id} value={category.id}>
                          {category.name}
                        </option>
                      ))}
                  </select>
                </div>
              </div>

              <div className="mt-4 space-y-3">
                {(filteredVariants.length ? filteredVariants : variants).map((variant) => (
                  <button
                    key={variant.id}
                    type="button"
                    onClick={() => setActiveVariantId(variant.id)}
                    className={`w-full rounded-[22px] border p-4 text-left ${
                      activeVariantId === variant.id ? 'border-[#0d6b66] bg-[#edf7f5]' : 'border-slate-200 bg-white'
                    }`}
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <div className="text-sm font-semibold text-slate-900">
                          {variant.vehicleCategory?.name || 'Vehicle'} | {variant.seatConfig?.name || 'Seats'}
                        </div>
                        <div className="mt-1 text-xs text-slate-600">Max pax {variant.maxPax}</div>
                        {variant.kmsIncluded ? <div className="mt-1 text-xs text-slate-500">{variant.kmsIncluded} km included</div> : null}
                      </div>
                      <div className="text-right text-sm font-semibold text-slate-900">
                        INR {Number(variant.priceTotal || 0).toLocaleString()}
                      </div>
                    </div>
                  </button>
                ))}
              </div>

              <Link to={enquiryLink} className="mt-4 inline-flex w-full justify-center rounded-full bg-slate-900 px-5 py-3 text-sm font-semibold text-white no-underline">
                Enquire about this package
              </Link>
            </div>
          </div>
        </div>
      </div>

      <div className="grid gap-6 lg:grid-cols-[1fr_320px]">
        <div className="space-y-6">
          <section className="rounded-[28px] border border-slate-200 bg-white p-5 shadow-sm">
            <h2 className="text-xl font-semibold text-slate-900">Overview</h2>
            <p className="mt-3 whitespace-pre-line text-sm leading-7 text-slate-700">
              {pkg.description || 'Package overview will appear here once configured from the admin panel.'}
            </p>
          </section>

          {highlightItems.length > 0 && (
            <section className="rounded-[28px] border border-slate-200 bg-white p-5 shadow-sm">
              <h2 className="text-xl font-semibold text-slate-900">Highlights</h2>
              <div className="mt-4 flex flex-wrap gap-2">
                {highlightItems.map((item) => (
                  <span key={item} className="rounded-full bg-[#f4ead7] px-3 py-2 text-sm text-[#865f1a]">
                    {item}
                  </span>
                ))}
              </div>
            </section>
          )}

          {(inclusionItems.length > 0 || exclusionItems.length > 0) && (
            <section className="grid gap-4 md:grid-cols-2">
              <div className="rounded-[28px] border border-slate-200 bg-white p-5 shadow-sm">
                <h2 className="text-xl font-semibold text-slate-900">Inclusions</h2>
                <ul className="mt-4 space-y-3 text-sm text-slate-700">
                  {inclusionItems.map((item) => (
                    <li key={item}>• {item}</li>
                  ))}
                </ul>
              </div>
              <div className="rounded-[28px] border border-slate-200 bg-white p-5 shadow-sm">
                <h2 className="text-xl font-semibold text-slate-900">Exclusions</h2>
                <ul className="mt-4 space-y-3 text-sm text-slate-700">
                  {exclusionItems.map((item) => (
                    <li key={item}>• {item}</li>
                  ))}
                </ul>
              </div>
            </section>
          )}

          {itineraryItems.length > 0 && (
            <section className="rounded-[28px] border border-slate-200 bg-white p-5 shadow-sm">
              <h2 className="text-xl font-semibold text-slate-900">Itinerary</h2>
              <div className="mt-4 space-y-4">
                {itineraryItems.map((item, index) => (
                  <div key={item.day || index} className="rounded-[22px] border border-slate-200 bg-[#fbfcfb] p-4">
                    <div className="text-xs font-semibold uppercase tracking-[0.18em] text-[#0d6b66]">
                      Day {item.day || index + 1}
                    </div>
                    <h3 className="mt-2 text-lg font-semibold text-slate-900">{item.title || `Day ${index + 1}`}</h3>
                    <p className="mt-2 whitespace-pre-line text-sm text-slate-700">{item.description || ''}</p>
                    {(item.meals || item.stayInfo || item.stay || item.image) && (
                      <div className="mt-3 flex flex-wrap gap-2 text-xs text-slate-500">
                        {item.meals ? <span className="rounded-full bg-white px-3 py-1">{item.meals}</span> : null}
                        {item.stayInfo || item.stay ? <span className="rounded-full bg-white px-3 py-1">{item.stayInfo || item.stay}</span> : null}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </section>
          )}

          {relatedPackages.length > 0 && (
            <section className="space-y-4">
              <h2 className="text-xl font-semibold text-slate-900">Related packages</h2>
              <div className="grid gap-4 md:grid-cols-3">
                {relatedPackages.map((item) => (
                  <article key={item.id} className="overflow-hidden rounded-[24px] border border-slate-200 bg-white shadow-sm">
                    <div className="h-40 bg-slate-100">
                      {item.coverImageUrl || item.media?.[0]?.url ? (
                        <img src={item.coverImageUrl || item.media?.[0]?.url} alt={item.title} className="h-full w-full object-cover" />
                      ) : (
                        <div className="flex h-full items-center justify-center text-xs text-slate-400">No image</div>
                      )}
                    </div>
                    <div className="p-4">
                      <h3 className="line-clamp-2 text-base font-semibold text-slate-900">{item.title}</h3>
                      <p className="mt-2 text-sm text-slate-600">{item.location?.name || 'Location'}</p>
                      <Link to={`/cabs/packages/${item.slug}`} className="mt-3 inline-flex rounded-full bg-slate-900 px-4 py-2 text-xs font-semibold text-white no-underline">
                        View package
                      </Link>
                    </div>
                  </article>
                ))}
              </div>
            </section>
          )}
        </div>

        <aside className="space-y-4">
          <div className="sticky top-24 rounded-[28px] border border-slate-200 bg-white p-5 shadow-sm">
            <p className="text-xs uppercase tracking-[0.18em] text-slate-400">Selected Variant</p>
            <h3 className="mt-2 text-lg font-semibold text-slate-900">
              {activeVariant?.vehicleCategory?.name || 'Choose a variant'}
            </h3>
            <div className="mt-4 space-y-2 text-sm text-slate-600">
              <div>Seats: {activeVariant?.seatConfig?.name || '-'}</div>
              <div>Max pax: {activeVariant?.maxPax || '-'}</div>
              <div>Price: {activeVariant ? `INR ${Number(activeVariant.priceTotal || 0).toLocaleString()}` : '-'}</div>
            </div>
            <Link to={enquiryLink} className="mt-4 inline-flex w-full justify-center rounded-full bg-[#0d6b66] px-5 py-3 text-sm font-semibold text-white no-underline">
              Request package quote
            </Link>
          </div>
        </aside>
      </div>

      <Modal show={Boolean(activeImage)} onHide={() => setActiveImage('')} centered size="lg">
        <Modal.Body className="p-0">
          {activeImage ? <img src={activeImage} alt={pkg.title} className="w-100" /> : null}
        </Modal.Body>
      </Modal>
    </section>
  );
};

export default CabsPackageDetail;
